import { useMemo } from 'react';
import { Props as ReactApexChartProps } from 'react-apexcharts';
import type { ExternalMonitoringPeriodBounds } from '../../../../../../helpers/external-monitoring-period';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import { AspectsPyramidCategoryToggles } from './aspects-pyramid-category-toggles';

function hashStringToInt(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function generateCategoryColor(categoryId: string): string {
  let hue = hashStringToInt(categoryId) % 360;
  const inRedRange = hue <= 20 || hue >= 340;
  const inGreenRange = hue >= 100 && hue <= 150;
  if (inRedRange) hue = (hue + 40) % 360;
  if (inGreenRange) hue = (hue + 60) % 360;
  return `hsl(${hue} 70% 45%)`;
}

export type PyramidFilterState = {
  enabledCategoryIds: string[];
  setEnabledCategoryIds: (ids: string[]) => void;
};

export type PyramidChartApi = {
  categories: { id: string; name: string }[];
  aspects: { categoryId: string; name: string; positive: number; negative: number }[];
};

export const EMPTY_PYRAMID_API: PyramidChartApi = {
  categories: [],
  aspects: [],
};

export const useAspectsPyramid = (
  filterState?: PyramidFilterState,
  api?: PyramidChartApi,
  _period?: ExternalMonitoringPeriodBounds,
): ChartProps => {
  const { categories, aspects: allAspects } = api ?? EMPTY_PYRAMID_API;
  /** Пустой массив = все категории (иначе при categoryId «все» с экрана приходит [] и график пустой) */
  const enabledRaw = filterState?.enabledCategoryIds;
  const enabledIds =
    enabledRaw === undefined || enabledRaw.length === 0
      ? categories.map((c) => c.id)
      : enabledRaw;
  const setEnabledIds = filterState?.setEnabledCategoryIds;
  const coloredCategories = useMemo(
    () => categories.map((c) => ({ ...c, color: generateCategoryColor(c.id) })),
    [categories],
  );

  const chartData = useMemo<ReactApexChartProps>(() => {
    const aspects = allAspects.filter((a) => enabledIds.includes(a.categoryId));
    const catColors = Object.fromEntries(
      coloredCategories
        .filter((c) => enabledIds.includes(c.id))
        .map((c) => [c.id, c.color]),
    );
    const labelColors = aspects.map((a) => catColors[a.categoryId] || '#6c757d');
    const POSITIVE_COLOR = '#12B76A';
    const NEGATIVE_COLOR = '#F04438';

    return {
      series: [
        { name: 'Положительные', data: aspects.map((a) => a.positive) },
        { name: 'Негативные', data: aspects.map((a) => -a.negative) },
      ],
      options: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '80%',
            borderRadius: 5,
            borderRadiusApplication: 'end',
            borderRadiusWhenStacked: 'all',
          },
        },
        colors: [POSITIVE_COLOR, NEGATIVE_COLOR],
        stroke: {
          width: 1,
          colors: ['#fff'],
        },
        grid: {
          xaxis: {
            lines: {
              show: false,
            },
          },
        },
        xaxis: {
          categories: aspects.map((a) => a.name),
          labels: {
            formatter: (value: string) => String(Math.abs(Math.round(Number(value)))),
          },
          title: {
            text: 'Количество',
          },
        },
        yaxis: {
          labels: {
            style: { colors: labelColors },
          },
        },
        tooltip: {
          shared: false,
          y: {
            formatter: (value: number) => String(Math.abs(value)),
          },
        },
        legend: {
          position: 'top',
          markers: {
            fillColors: [POSITIVE_COLOR, NEGATIVE_COLOR],
          },
        },
        dataLabels: { enabled: false },
      },
    };
  }, [allAspects, coloredCategories, enabledIds, _period?.begin, _period?.end]);

  const footer =
    setEnabledIds &&
    (
      <AspectsPyramidCategoryToggles
        categories={coloredCategories}
        enabledIds={enabledIds}
        onChange={setEnabledIds}
      />
    );

  return {
    ...chartData,
    label: 'Аспекты по тональности',
    subLabel: api
      ? undefined
      : 'Положительные и негативные оценки по аспектам. Негативные значения отображаются влево, положительные — вправо. Цвет подписи аспекта — категория; цвет столбиков — тональность.',
    type: 'bar',
    fullWidth: true,
    footer,
  };
};
